// Campus dashboard — hero banner + 4 stat tiles + recent activity feed
// (best-effort, sourced from admin_audit_log) + featured open cases the
// school's students could join. Mirrors AdminDashboard's shape but
// scoped to the caller's university.

import type { Href } from "expo-router";
import { COLORS } from "../../../lib/brand";
import { router, useFocusEffect } from "expo-router";
import { useCallback, useMemo, useRef, useState } from "react";
import { ActivityIndicator, Platform, Pressable, Text, View, type ViewStyle } from "react-native";

import { useAuth } from "../../../lib/authContext";
import {
  getMyUniversityProfile,
  listFeaturedOpenCases,
  listMyCompetitions,
  listMyStudentsWithVerification,
  type CampusStudentDetail,
  type UniversityProfile,
} from "../../../lib/campus";
import type { CampusCompetitionRow } from "../../../types/database";

const BLACK = COLORS.BLACK.hex;
const FOREST = COLORS.FOREST.hex;
const FOREST_MID = COLORS.FOREST_MID.hex;
const AMBER = COLORS.AMBER.hex;
const SAGE = COLORS.SAGE.hex;
const LINEN = COLORS.LINEN.hex;
const W = "rgba(255,255,255,1)";
const WS = "rgba(255,255,255,0.78)";
const WM = "rgba(255,255,255,0.55)";
const WL = "rgba(255,255,255,0.28)";
const BORDER = "rgba(255,255,255,0.07)";

void FOREST;
void W;

function formatCents(cents: number | null | undefined): string {
  if (cents == null || cents === 0) return "—";
  const n = cents / 100;
  return `$${n.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function firstName(full: string | null | undefined): string {
  if (!full?.trim()) return "there";
  return full.trim().split(/\s+/)[0];
}

export default function CampusDashboardScreen() {
  const { user } = useAuth();
  const [profile, setProfile] = useState<UniversityProfile | null>(null);
  const [students, setStudents] = useState<CampusStudentDetail[]>([]);
  const [comps, setComps] = useState<CampusCompetitionRow[]>([]);
  const [featured, setFeatured] = useState<
    Array<{ id: string; name: string; company_name: string | null; engagement_model: string; scholarship_cents: number; closes_at: string | null }>
  >([]);
  const [loading, setLoading] = useState(true);
  const hasLoadedRef = useRef(false);

  const load = useCallback(async () => {
    if (!hasLoadedRef.current) setLoading(true);
    try {
      const [p, s, c, f] = await Promise.all([
        getMyUniversityProfile(),
        listMyStudentsWithVerification(),
        listMyCompetitions(),
        listFeaturedOpenCases(),
      ]);
      setProfile(p);
      setStudents(s);
      setComps(c);
      setFeatured(f);
    } finally {
      hasLoadedRef.current = true;
      setLoading(false);
    }
  }, []);

  useFocusEffect(useCallback(() => { void load(); }, [load]));

  const stats = useMemo(() => {
    const verified = students.filter((s) => s.school_verified).length;
    const unverified = students.length - verified;
    const activeCases = comps.filter((c) => c.status === "open" || c.status === "judging").length;
    return { verified, unverified, activeCases, total: students.length };
  }, [students, comps]);

  const accent = profile?.primary_color_hex && profile.primary_color_hex.length > 0
    ? profile.primary_color_hex
    : AMBER;

  return (
    <View style={{ padding: 24, maxWidth: 1080 }}>
      {/* Hero banner */}
      <View
        style={{
          padding: 22,
          borderRadius: 6,
          backgroundColor: FOREST_MID,
          borderWidth: 1,
          borderColor: BORDER,
        }}
      >
        <Text style={{ fontFamily: "DMSans_700Bold", fontSize: 10, letterSpacing: 3, textTransform: "uppercase", color: accent }}>
          Campus portal
        </Text>
        <Text style={{ marginTop: 6, fontFamily: "CormorantGaramond_300Light", fontSize: 30, lineHeight: 34, color: LINEN }}>
          Welcome back, {firstName(user?.user_metadata?.full_name as string | undefined)}.
        </Text>
        <Text style={{ marginTop: 4, fontFamily: "DMSans_300Light", fontSize: 13, color: WM }}>
          Tracking {stats.total} student{stats.total === 1 ? "" : "s"} across {comps.length} Coravu case{comps.length === 1 ? "" : "s"}
          {profile?.name ? ` at ${profile.name}` : ""}.
        </Text>
      </View>

      {/* Stat tiles */}
      <View style={{ marginTop: 16, flexDirection: "row", flexWrap: "wrap", gap: 10 }}>
        <StatTile label="Verified students" value={stats.verified} tone={accent} />
        <StatTile label="Pending review" value={stats.unverified} sublabel="Awaiting verification" />
        <StatTile label="Active cases" value={stats.activeCases} sublabel={`${comps.length} total`} />
        <StatTile label="Total students" value={stats.total} />
      </View>

      {/* Verification queue snippet */}
      {stats.unverified > 0 ? (
        <Pressable
          onPress={() => router.push("/campus/students" as Href)}
          style={[
            {
              marginTop: 16,
              padding: 14,
              borderRadius: 6,
              borderWidth: 1,
              borderColor: "rgba(181,135,58,0.40)",
              backgroundColor: "rgba(181,135,58,0.08)",
              flexDirection: "row",
              alignItems: "center",
              gap: 12,
            } as ViewStyle,
            Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
          ]}
        >
          <View style={{ flex: 1 }}>
            <Text style={{ fontFamily: "DMSans_700Bold", fontSize: 11, letterSpacing: 1.4, textTransform: "uppercase", color: accent }}>
              {stats.unverified} student{stats.unverified === 1 ? "" : "s"} awaiting verification
            </Text>
            <Text style={{ marginTop: 4, fontFamily: "DMSans_400Regular", fontSize: 12.5, color: WS }}>
              Vouch for unverified students to help Coravu admins fast-track their review.
            </Text>
          </View>
          <Text style={{ fontFamily: "DMSans_700Bold", fontSize: 11, letterSpacing: 1.2, color: accent }}>
            Review →
          </Text>
        </Pressable>
      ) : null}

      {/* Featured cases */}
      <Text style={{ marginTop: 24, fontFamily: "DMSans_700Bold", fontSize: 9.5, letterSpacing: 1.6, textTransform: "uppercase", color: WM, marginBottom: 8 }}>
        Open cases your students could join
      </Text>
      {loading ? (
        <ActivityIndicator size="small" color={accent} />
      ) : featured.length === 0 ? (
        <Empty
          title="No open cases right now."
          body="When a Coravu client posts a case that's open to your school, it will appear here."
        />
      ) : (
        <View style={{ gap: 8 }}>
          {featured.map((c) => (
            <Pressable
              key={c.id}
              onPress={() => router.push(`/campus/cases?case=${c.id}` as Href)}
              style={[
                {
                  padding: 14,
                  borderRadius: 6,
                  backgroundColor: FOREST_MID,
                  borderWidth: 1,
                  borderColor: BORDER,
                } as ViewStyle,
                Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
              ]}
            >
              <Text style={{ fontFamily: "DMSans_500Medium", fontSize: 13.5, color: LINEN }}>{c.name}</Text>
              <Text style={{ marginTop: 2, fontFamily: "DMSans_400Regular", fontSize: 11.5, color: WS }}>
                {c.company_name ?? "—"} · {c.engagement_model === "rm" ? "Coravu RM" : "Licensing"}
                {c.scholarship_cents > 0 ? ` · ${formatCents(c.scholarship_cents)} scholarship` : ""}
                {c.closes_at ? ` · closes ${new Date(c.closes_at).toLocaleDateString()}` : ""}
              </Text>
            </Pressable>
          ))}
        </View>
      )}

      {/* Engagement summary card */}
      <Text style={{ marginTop: 24, fontFamily: "DMSans_700Bold", fontSize: 9.5, letterSpacing: 1.6, textTransform: "uppercase", color: WM, marginBottom: 8 }}>
        Your students by case
      </Text>
      {comps.length === 0 ? (
        <Empty
          title="No cases yet."
          body={`When students from ${profile?.name ?? "your school"} join Coravu cases, they'll show up here.`}
        />
      ) : (
        <View style={{ gap: 8 }}>
          {comps.slice(0, 6).map((c) => (
            <View
              key={c.competition_id}
              style={{
                padding: 12,
                borderRadius: 6,
                backgroundColor: FOREST_MID,
                borderWidth: 1,
                borderColor: BORDER,
                flexDirection: "row",
                alignItems: "center",
                gap: 12,
              }}
            >
              <View style={{ flex: 1 }}>
                <Text style={{ fontFamily: "DMSans_500Medium", fontSize: 12.5, color: LINEN }}>{c.competition_name}</Text>
                <Text style={{ marginTop: 2, fontFamily: "DMSans_400Regular", fontSize: 11, color: WM }}>
                  {c.company_name ?? "—"} · status {c.status}
                </Text>
              </View>
              <View style={{ flexDirection: "row", gap: 12 }}>
                <Mini label="Registered" value={c.participating_students ?? 0} />
                <Mini label="Submitted" value={c.submitted_students ?? 0} />
              </View>
            </View>
          ))}
          {comps.length > 6 ? (
            <Pressable
              onPress={() => router.push("/campus/cases" as Href)}
              style={Platform.OS === "web" ? ({ cursor: "pointer" } as const) : undefined}
            >
              <Text style={{ marginTop: 4, fontFamily: "DMSans_500Medium", fontSize: 11.5, color: accent }}>
                See all {comps.length} cases →
              </Text>
            </Pressable>
          ) : null}
        </View>
      )}
    </View>
  );
}

function StatTile({
  label,
  value,
  sublabel,
  tone,
}: {
  label: string;
  value: number;
  sublabel?: string;
  tone?: string;
}) {
  return (
    <View
      style={{
        flexBasis: "23%",
        flexGrow: 1,
        minWidth: 160,
        padding: 14,
        borderRadius: 6,
        backgroundColor: FOREST_MID,
        borderWidth: 1,
        borderColor: tone ? "rgba(181,135,58,0.32)" : BORDER,
      }}
    >
      <Text
        style={{
          fontFamily: "DMSans_700Bold",
          fontSize: 9.5,
          letterSpacing: 1.6,
          textTransform: "uppercase",
          color: tone ?? WM,
        }}
      >
        {label}
      </Text>
      <Text style={{ marginTop: 4, fontFamily: "CormorantGaramond_300Light", fontSize: 26, color: LINEN }}>
        {value.toLocaleString()}
      </Text>
      {sublabel ? (
        <Text style={{ marginTop: 2, fontFamily: "DMSans_400Regular", fontSize: 10.5, color: WL }}>
          {sublabel}
        </Text>
      ) : null}
    </View>
  );
}

function Mini({ label, value }: { label: string; value: number | string }) {
  return (
    <View style={{ alignItems: "flex-end" }}>
      <Text style={{ fontFamily: "DMSans_700Bold", fontSize: 9, letterSpacing: 1.2, textTransform: "uppercase", color: WM }}>{label}</Text>
      <Text style={{ marginTop: 2, fontFamily: "CormorantGaramond_500Medium", fontSize: 18, color: LINEN }}>{value}</Text>
    </View>
  );
}

function Empty({ title, body }: { title: string; body: string }) {
  return (
    <View style={{ padding: 22, borderRadius: 6, backgroundColor: FOREST_MID, borderWidth: 1, borderColor: BORDER, alignItems: "center" }}>
      <Text style={{ fontFamily: "CormorantGaramond_500Medium", fontSize: 16, color: LINEN }}>{title}</Text>
      <Text style={{ marginTop: 4, fontFamily: "DMSans_400Regular", fontSize: 12, color: WM, textAlign: "center", maxWidth: 480 }}>
        {body}
      </Text>
    </View>
  );
}
